import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { userAPI } from '../services/api';
import { OTPModal } from './OTPModal';
import { otpService } from '../services/otpService';

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showOTP, setShowOTP] = useState(false);
  const [otpPhone, setOtpPhone] = useState('');
  const [otpPurpose, setOtpPurpose] = useState('');
  const [pendingAction, setPendingAction] = useState<() => void>(() => {});
  const [newUser, setNewUser] = useState({ username: '', email: '', phone: '', role: 'view_only' as 'master_admin' | 'view_only' });
  const [editUser, setEditUser] = useState({ username: '', email: '', password: '', phone: '', role: 'view_only' as 'master_admin' | 'view_only' });
  const [confirmDelete, setConfirmDelete] = useState<User | null>(null);

  const getCurrentUserRole = (): string => {
    const user = localStorage.getItem('user');
    if (user) return JSON.parse(user).role || 'view_only';
    return 'view_only';
  };

  const currentUserRole = getCurrentUserRole();
  const canEdit = currentUserRole === 'master_admin';

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try { const response = await userAPI.getAll(); setUsers(response.data); }
    catch (error) { console.error('Error fetching users:', error); }
    finally { setLoading(false); }
  };

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await userAPI.create(newUser);
      setNewUser({ username: '', email: '', phone: '', role: 'view_only' });
      setShowInviteForm(false);
      if (response.data.invitation_sent) {
        alert(`Invitation sent to ${newUser.email}!`);
      } else {
        alert(`User created but email failed. Temp password: ${response.data.temp_password}`);
      }
      fetchUsers();
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    } finally { setLoading(false); }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditUser({ username: user.username, email: user.email || '', password: '', phone: user.phone || '', role: user.role || 'view_only' });
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setLoading(true);
    try {
      const updateData: any = { username: editUser.username, email: editUser.email, role: editUser.role };
      if (editUser.password.trim()) updateData.password = editUser.password;
      await userAPI.update(editingUser.id!, updateData);
      setEditingUser(null);
      fetchUsers();
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    } finally { setLoading(false); }
  };

  const handleDeleteUser = async (user: User) => { setConfirmDelete(user); };

  const executeDeleteUser = async () => {
    if (!confirmDelete) return;
    try {
      await userAPI.delete(confirmDelete.id!);
      setConfirmDelete(null);
      fetchUsers();
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleOTPVerify = async (code: string): Promise<boolean> => {
    try { const isValid = await otpService.verifyOTP(otpPhone, code); if (isValid) { pendingAction(); return true; } return false; }
    catch { return false; }
  };
  const handleOTPResend = async (): Promise<void> => { await otpService.sendOTP(otpPhone, otpPurpose); };
  const handleOTPClose = () => { setShowOTP(false); setPendingAction(() => {}); setOtpPhone(''); setOtpPurpose(''); };

  const admins = users.filter(u => u.role === 'master_admin');
  const viewOnly = users.filter(u => u.role !== 'master_admin');

  const inputStyle: React.CSSProperties = { width: '100%', padding: '12px 14px', borderRadius: '8px', border: '2px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box' };
  const labelStyle: React.CSSProperties = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' };

  const modalOverlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' };
  const modalBox: React.CSSProperties = { background: '#fff', borderRadius: '16px', maxWidth: '480px', width: '92%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' };
  const modalHeader: React.CSSProperties = { padding: '20px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
  const modalBody: React.CSSProperties = { padding: '24px' };

  const UserCard = ({ user }: { user: User }) => (
    <div style={{
      background: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #e5e7eb',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'box-shadow 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
    }}
    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
    onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div style={{
          width: '44px', height: '44px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: 700, color: '#fff',
          background: user.role === 'master_admin' ? 'linear-gradient(135deg, #2563eb, #7c3aed)' : 'linear-gradient(135deg, #6b7280, #9ca3af)'
        }}>
          {user.username.charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: '15px', color: '#1f2937', marginBottom: '2px' }}>{user.username}</div>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>{user.email}</div>
          {user.phone && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>📱 {user.phone}</div>}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{
          padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.3px',
          background: user.role === 'master_admin' ? '#ede9fe' : '#f3f4f6',
          color: user.role === 'master_admin' ? '#7c3aed' : '#6b7280'
        }}>
          {user.role === 'master_admin' ? '👑 Admin' : '👁️ View Only'}
        </span>
        {canEdit && (
          <>
            <button onClick={() => handleEditUser(user)} style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '13px', cursor: 'pointer', color: '#374151', fontWeight: 500 }}>Edit</button>
            <button onClick={() => handleDeleteUser(user)} style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #fecaca', background: '#fef2f2', fontSize: '13px', cursor: 'pointer', color: '#dc2626', fontWeight: 500 }}>Delete</button>
          </>
        )}
      </div>
    </div>
  );

  if (loading && users.length === 0) {
    return <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>Loading staff...</div>;
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: 700, margin: 0, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>👥 Staff Management</h1>
          <p style={{ color: '#888', margin: '4px 0 0 0', fontSize: '14px' }}>
            {canEdit ? 'Manage staff accounts and portal access' : 'View staff accounts (read-only)'}
          </p>
        </div>
        {canEdit && (
          <button onClick={() => setShowInviteForm(true)} style={{ padding: '12px 24px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '14px', boxShadow: '0 2px 8px rgba(37,99,235,0.3)' }}>
            + Invite Staff
          </button>
        )}
      </div>

      {/* KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Total Staff', value: users.length, icon: '👥', color: '#2563eb' },
          { label: 'Admins', value: admins.length, icon: '👑', color: '#7c3aed' },
          { label: 'View Only', value: viewOnly.length, icon: '👁️', color: '#6b7280' },
        ].map((t, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '12px', padding: '16px', textAlign: 'center', border: `2px solid ${t.color}20`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: '22px', marginBottom: '4px' }}>{t.icon}</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: t.color }}>{t.value}</div>
            <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t.label}</div>
          </div>
        ))}
      </div>

      {/* Admin section */}
      {admins.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', paddingLeft: '4px' }}>Administrators</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {admins.map(u => <UserCard key={u.id} user={u} />)}
          </div>
        </div>
      )}

      {/* View only section */}
      {viewOnly.length > 0 && (
        <div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', paddingLeft: '4px' }}>View Only</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {viewOnly.map(u => <UserCard key={u.id} user={u} />)}
          </div>
        </div>
      )}

      {users.length === 0 && (
        <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af', background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>👥</div>
          <p style={{ margin: 0, fontSize: '15px' }}>No staff users yet. Invite your first team member!</p>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteForm && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div style={modalHeader}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1f2937' }}>Invite Staff Member</h3>
              <button onClick={() => setShowInviteForm(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>×</button>
            </div>
            <div style={modalBody}>
          <form onSubmit={handleInviteUser} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Username *</label>
              <input type="text" value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Email *</label>
              <input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input type="tel" value={newUser.phone} onChange={e => setNewUser({ ...newUser, phone: e.target.value })} placeholder="+64 21 123 4567" style={inputStyle} />
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>A temp password will be emailed to them</div>
            </div>
            <div>
              <label style={labelStyle}>Role *</label>
              <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value as any })} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="view_only">👁️ View Only — Can view data, no changes</option>
                <option value="master_admin">👑 Admin — Full access</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button type="button" onClick={() => setShowInviteForm(false)} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '14px', color: '#6b7280' }}>Cancel</button>
              <button type="submit" disabled={loading} style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>
                {loading ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingUser && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div style={modalHeader}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1f2937' }}>Edit Staff Member</h3>
              <button onClick={() => setEditingUser(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>×</button>
            </div>
            <div style={modalBody}>
          <form onSubmit={handleUpdateUser} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Username *</label>
              <input type="text" value={editUser.username} onChange={e => setEditUser({ ...editUser, username: e.target.value })} required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Email *</label>
              <input type="email" value={editUser.email} onChange={e => setEditUser({ ...editUser, email: e.target.value })} required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input type="tel" value={editUser.phone} onChange={e => setEditUser({ ...editUser, phone: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Role *</label>
              <select value={editUser.role} onChange={e => setEditUser({ ...editUser, role: e.target.value as any })} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="view_only">👁️ View Only</option>
                <option value="master_admin">👑 Admin</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>New Password</label>
              <input type="password" value={editUser.password} onChange={e => setEditUser({ ...editUser, password: e.target.value })} placeholder="Leave blank to keep current" style={inputStyle} />
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Leave blank to keep current password</div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button type="button" onClick={() => setEditingUser(null)} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '14px', color: '#6b7280' }}>Cancel</button>
              <button type="submit" disabled={loading} style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {confirmDelete && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div style={modalHeader}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1f2937' }}>Delete Staff Member</h3>
              <button onClick={() => setConfirmDelete(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>×</button>
            </div>
            <div style={modalBody}>
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
            <p style={{ fontSize: '15px', color: '#374151', margin: '0 0 4px 0' }}>Are you sure you want to delete</p>
            <p style={{ fontSize: '18px', fontWeight: 700, color: '#dc2626', margin: '0 0 16px 0' }}>{confirmDelete.username}?</p>
            <p style={{ fontSize: '13px', color: '#9ca3af', margin: '0 0 24px 0' }}>This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={() => setConfirmDelete(null)} style={{ padding: '10px 24px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '14px', color: '#6b7280' }}>Cancel</button>
              <button onClick={executeDeleteUser} style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>Delete</button>
            </div>
          </div>
            </div>
          </div>
        </div>
      )}

      <OTPModal isOpen={showOTP} onClose={handleOTPClose} onVerify={handleOTPVerify} phone={otpPhone} purpose={otpPurpose} onResend={handleOTPResend} />
    </div>
  );
};

export default Users;
